// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The XR-030 recycle, as a build-time setting — and off unless a build says otherwise (13 Sep 2026).
//
// `sessionSafety.ts` is the mitigation Chess VR wrote in July 2026 for a Horizon OS v2.5 defect: in
// an immersive session VRShell launched `com.oculus.panelapp.controlbar.DefaultControlBarActivity`
// as a new Android task every ~5.2 seconds, and at fifty recent tasks ActivityManager evicted the
// Browser (`BrowserActivity, finish-imm:recent-task-trimmed`) — the page and the game gone about four
// minutes in, on Browser 149.0, on the official Immersive Web sample as much as on ours. Ending the
// session at 165 seconds kept a first clean session under the threshold. 06 §7 inherited it, with
// `.env.example` promising the switch that `VITE_PINBALL_XR_SAFETY_RECYCLE_SECONDS` names — a
// promise nothing read, so every session ended at 165 seconds and no build could say otherwise.
//
// **What the second device day of M13 found, on the headset the defect was found on.** Horizon OS
// v2.7 (build 5243367.0036.520), Browser 150.1.0.24.52: `dumpsys activity` lists no
// `DefaultControlBarActivity` at all — the shell runs one `ControlBarActivity` task — Recents held
// 43 old persisted tasks and grew by nothing across a day of immersive sessions, a 330-second Build
// session ran past the four-minute window with nothing evicted, and the same page entered immersive
// twice (the other v2.5 defect). The mechanism the recycle guards against is not on this build, and
// the product owner's requirement is the one 06 §7 always meant to reach: *the player plays as long
// as they want, without being interrupted.*
//
// So the setting is read, and **unset is off**. A deployment that has to run on an affected Horizon
// build sets the seconds and gets the recycle back exactly as it was — the mitigation is kept, not
// deleted, because 13 §8's gate is still the honest test and nobody has run its 60-minute cell.
//
// Read at the point of use rather than captured at module load, for `flag.ts`'s reason.

import { WARN_FOR_SECONDS } from './sessionSafety'

export const RECYCLE_SECONDS_VAR = 'VITE_PINBALL_XR_SAFETY_RECYCLE_SECONDS'
export const WARNING_SECONDS_VAR = 'VITE_PINBALL_XR_SAFETY_WARNING_SECONDS'

/** What the runtime is told about the watchdog. */
export interface RecycleSetting {
  /** Whether the session is recycled at all. */
  readonly recycle: boolean
  /** Seconds of visible presence before it is, when it is. */
  readonly afterSeconds?: number
  /** How long the camera-following warning shows before it, when it is. */
  readonly warnSeconds?: number
}

function positiveSeconds(name: string): number | undefined {
  const raw = (import.meta.env as unknown as Record<string, unknown>)[name]
  if (typeof raw !== 'string') return undefined
  const value = Number(raw.trim())
  if (!Number.isFinite(value) || value <= 0) return undefined
  return value
}

/**
 * The recycle setting for this build: off unless `VITE_PINBALL_XR_SAFETY_RECYCLE_SECONDS` is a
 * positive number of seconds. The warning defaults to `WARN_FOR_SECONDS` and is only read when the
 * recycle is on, because a warning for nothing is a sentence for nothing.
 */
export function recycleSetting(): RecycleSetting {
  const afterSeconds = positiveSeconds(RECYCLE_SECONDS_VAR)
  if (afterSeconds === undefined) return { recycle: false }
  return {
    recycle: true,
    afterSeconds,
    warnSeconds: positiveSeconds(WARNING_SECONDS_VAR) ?? WARN_FOR_SECONDS,
  }
}
